import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, of, map, tap, delay } from 'rxjs';
import { User } from '../models/user.model';
import { LoginRequest, LoginData, AuthUser, TokenResponse, PermissionResponse } from '../models/auth.model';
import { ApiResponse } from '../models/api-response.model';
import { StorageService } from './storage.service';

const MOCK_CREDENTIALS = {
  email: 'ricardosola2012@gmail.com',
  password: 'ricardo123',
};

const USE_MOCK = false;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);

  private readonly _currentUser = signal<User | null>(null);

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);
  readonly userRole = computed(() => this._currentUser()?.role ?? null);
  readonly userPermissions = computed(() => this._currentUser()?.permissions ?? []);

  getToken(): string | null {
    return this.storage.getToken();
  }

  private determineEndpoint(loginType?: string | null): string {
    if (!loginType) return '/api/v1/auth/login';
    return `/api/v1/auth/login/external/${loginType}`;
  }

  private extractPermissionCodes(menu: PermissionResponse[] | null | undefined): string[] {
    const codes: string[] = [];
    const walk = (items: PermissionResponse[]) => {
      for (const item of items) {
        codes.push(item.code);
        if (item.children) walk(item.children);
      }
    };
    walk(menu ?? []);
    return codes;
  }

  private buildFullName(data: LoginData): string {
    const name = data.user.name?.trim();
    if (name) {
      return name;
    }
    return data.user.email;
  }

  private mapToUser(data: LoginData): User {
    return {
      id: String(data.user.user_id),
      email: data.user.email,
      name: this.buildFullName(data),
      role: data.user.role.code,
      permissions: this.extractPermissionCodes(data.menu),
      medalUrl: data.user.medalUrl ?? null,
      range: data.user.range ?? null,
    };
  }

  private mapToAuthUser(data: LoginData): AuthUser {
    return {
      id: data.user.user_id,
      email: data.user.email,
      name: this.buildFullName(data),
      role: data.user.role.code,
      permissions: this.extractPermissionCodes(data.menu),
      menu: data.menu ?? [],
      medalUrl: data.user.medalUrl ?? null,
      range: data.user.range ?? null,
    };
  }

  login(credentials: LoginRequest): Observable<{ token: string; user: AuthUser }> {
    if (USE_MOCK) {
      const emailOk = credentials.email === MOCK_CREDENTIALS.email;
      const passOk = credentials.password === MOCK_CREDENTIALS.password;
      if (!emailOk) {
        return throwError(() => ({ type: 'invalid_email' as const, message: 'Usuario incorrecto' })).pipe(delay(600));
      }
      if (!passOk) {
        return throwError(() => ({ type: 'invalid_password' as const, message: 'Contraseña incorrecta' })).pipe(delay(600));
      }
      const result = {
        token: 'mock-jwt-token-xxx',
        user: { id: 1, email: MOCK_CREDENTIALS.email, name: 'Ricardo Sola', role: 'STUDENT' as const, permissions: [] as string[] },
      };
      return of(result).pipe(
        delay(600),
        tap((res) => {
          this.storage.setToken(res.token);
          this.storage.setRefreshToken('mock-refresh-token-yyy');
          this._currentUser.set({
            id: String(res.user.id),
            email: res.user.email,
            name: res.user.name,
            role: 'STUDENT',
            permissions: [],
          });
        })
      );
    }

    const endpoint = this.determineEndpoint(credentials.loginType);
    const body: Record<string, string | null> = {
      email: credentials.email,
      password: credentials.password,
    };
    if (!credentials.loginType) {
      body['loginType'] = null;
    }
    return this.http.post<ApiResponse<LoginData>>(endpoint, body).pipe(
      map(response => {
        if (!response.state) throw { type: 'generic' as const, message: response.message };
        return response.data;
      }),
      tap(data => {
        this.storage.setToken(data.token.access_token);
        this.storage.setRefreshToken(data.token.refresh_token);
        this._currentUser.set(this.mapToUser(data));
      }),
      map(data => ({
        token: data.token.access_token,
        user: this.mapToAuthUser(data),
      }))
    );
  }

  logout(sessionExpired: boolean = false): void {
    this.http.post<ApiResponse<unknown>>('/api/v1/auth/logout', {}).subscribe({
      next: () => {
        this.clearLocalSession();
        this.router.navigate(['/auth/login'], sessionExpired ? { queryParams: { expired: true } } : undefined);
      },
      error: () => {
        this.clearLocalSession();
        this.router.navigate(['/auth/login'], sessionExpired ? { queryParams: { expired: true } } : undefined);
      },
    });
  }

  private clearLocalSession(): void {
    this.storage.clearAll();
    this._currentUser.set(null);
  }

  refreshToken(): Observable<void> {
    const refreshToken = this.storage.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('No refresh token'));

    return this.http.post<ApiResponse<TokenResponse>>('/api/v1/auth/refresh', { refreshToken }).pipe(
      map(response => {
        if (!response.state) throw new Error(response.message);
        return response.data;
      }),
      tap(data => {
        this.storage.setToken(data.access_token);
        this.storage.setRefreshToken(data.refresh_token);
      }),
      map(() => void 0)
    );
  }

  hasPermission(permission: string): boolean {
    return this.userPermissions().includes(permission);
  }

  hasRole(role: string): boolean {
    return this.userRole() === role;
  }

  isTokenValid(): boolean {
    const token = this.storage.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  getUserIdentityStatus(idUser: number): Observable<ApiResponse<{ idUser: number; idStateFk: number }>> {
    return this.http.get<ApiResponse<{ idUser: number; idStateFk: number }>>(`/api/v1/user/${idUser}`);
  }
}
